import * as React from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import { useTranslation } from 'react-i18next';

import ProductCard, {
  CardImage,
  CardSubSubTitle,
} from '../../components/ProductCard/ProductCard';
import { CreateBundleContainer } from '../../components/Bundle/BundleBoxStructure';

import AddonsModalContentSections from './AddonsModalContentSections';
import { addonIsBaB, AddonProduct } from './useAddons';
import { showMinMaxPrice } from '../utils';
import Button from '../../components/Button';
import { Paragraph } from '../../components/Typography';
import { useGlobalContext } from '../state/GlobalContext';
import { FiltersSection } from '../../bundles/CreateBundle/FiltersSection';
import { useFilters } from '../../bundles/CreateBundle/useFilters';
import { VariantStyle } from '../../../components/BundleSettings/types';

const CURRENCY = get(window, ['Shopify', 'currency', 'active'], 'USD');

interface AddonsModalContentProps {
  addons: AddonProduct[];
  selectAddon: (id: number, isBundle: boolean) => void;
}

const AddonsModalContent = (
  props: AddonsModalContentProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const { customerPortalSettings } = useGlobalContext();
  const addonProducts = React.useMemo(
    () => props.addons.map(({ productData }) => productData),
    [props.addons],
  );

  const hasAddonsSectionsActive =
    !!customerPortalSettings?.addonsAndSwapModalHasSections;
  const hasFiltersActive =
    !!customerPortalSettings?.addonsAndSwapModalFiltersActive;
  const addonFilters =
    customerPortalSettings?.addonsAndSwapModalFilterGroups || [];

  const filtersConfig = {
    VariantStyle: VariantStyle.split,
    BuildABoxFilterGroups: addonFilters,
  };

  const {
    filteredProducts,
    selectedUrlFilterIds,
    setSelectedFilters,
    selectedFilters,
    handleResetFilters,
  } = useFilters({
    productsInCollection: addonProducts,
    config: filtersConfig,
  });

  if (hasAddonsSectionsActive) {
    return (
      <ModalSectionsContainer>
        {hasFiltersActive ? (
          <FiltersSection
            selectedUrlFilterIds={selectedUrlFilterIds}
            setSelectedFilters={setSelectedFilters}
            selectedFilters={selectedFilters}
            handleResetFilters={handleResetFilters}
            filters={addonFilters}
          />
        ) : null}
        <AddonsModalContentSections
          addons={filteredProducts as GlobalProduct[]}
          selectAddon={props.selectAddon}
        />
        ;
      </ModalSectionsContainer>
    );
  }

  return (
    <CreateBundleContainer data-testid="addons-modal">
      {hasFiltersActive ? (
        <FiltersSection
          selectedUrlFilterIds={selectedUrlFilterIds}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          handleResetFilters={handleResetFilters}
          filters={addonFilters}
        />
      ) : null}
      <main className="addons-modal-content">
        <>
          {(filteredProducts as GlobalProduct[]).map((addon: GlobalProduct) => (
            <ProductCard key={addon.id} id={`${addon.id}`}>
              <CardImage image={addon.image} alt={addon.title} />
              <CardSubSubTitle
                className="bundle-product-card__subtitle"
                modifiers={['body']}
              >
                {addon.title}
              </CardSubSubTitle>
              <Button
                fullWidth
                filled
                onClick={() =>
                  props.selectAddon(parseInt(`${addon.id}`), addonIsBaB(addon))
                }
                disabled={!addon.available}
              >
                {addon.available
                  ? addonIsBaB(addon)
                    ? t('product_swap.customize_bundle')
                    : t('addons.select')
                  : t('product_swap.sold_out')}
              </Button>
              <Paragraph
                modifiers={{
                  body: true,
                  subdued: !!addon.discountedPrice,
                }}
                style={{
                  marginTop: 20,
                  textDecoration: addon.discountedPrice
                    ? 'line-through'
                    : 'none',
                }}
              >
                {parseInt(addon.priceMax as string) === 0 &&
                parseInt(addon.priceMin as string) === 0
                  ? null
                  : showMinMaxPrice({
                      currency: addon.priceCurrency || CURRENCY,
                      max: addon.priceMax as number,
                      min: addon.priceMin as number,
                    })}
              </Paragraph>

              {!isNull(addon.discountedPrice) ||
              !isNaN(addon.discountedPrice) ? (
                <DiscountedPrice>
                  {showMinMaxPrice({
                    currency: addon.priceCurrency || CURRENCY,
                    max: addon.discountedPriceMax as number,
                    min: addon.discountedPriceMin as number,
                  })}
                </DiscountedPrice>
              ) : null}
            </ProductCard>
          ))}
        </>
      </main>
    </CreateBundleContainer>
  );
};

const DiscountedPrice = styled.span.attrs({
  className: 'discounted-addons-price',
})`
  font-weight: 600;
  font-size: 16px;
`;

const ModalSectionsContainer = styled.div.attrs({
  className: 'addons-scrollable-section',
})`
  padding: 2rem 4rem;
  padding-bottom: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  .awt-filters-container {
    margin: 2rem auto;
    width: fit-content;
    position: relative;
    overflow: visible;
  }
  main {
    margin: 2rem auto;
    margin-top: 4rem;
    width: 100%;
    display: grid;
    align-items: stretch;
    justify-items: stretch;
    grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
    grid-auto-rows: min-content;
    overflow-y: auto;
    scroll-behavior: smooth;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
    row-gap: 4rem;
  }
`;

export default AddonsModalContent;
